import PropTypes from 'prop-types';
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { clearTransactAsData, selector } from 'reducers/transactAs';
import useEnrolledPalLaunch from 'hooks/useEnrolledPalLaunch';
import PaymentIframe from '../../PalGuestPayment/PaymentIframe';
import {
  buildIfpPalPaymentPopupFeatures,
  IFP_PAL_PAYMENT_POPUP_WINDOW_NAME,
} from '../../PalGuestPayment/popupConstants';

export { resolveEnrolledPalContext } from 'hooks/useEnrolledPalLaunch';

const useStyle = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  body: {
    paddingTop: theme.spacing(1),
    minHeight: theme.spacing(20),
  },
}));

function getPopupLoadingUrl() {
  return `${window.location.origin}/ifpPalPaymentLoading.html`;
}

const EnrolledPalAfterRegister = ({ payerName, onCancel, fspId }) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const transactAsData = useSelector(selector) || {};
  const { registerData, data: transactPayload = {} } = transactAsData;
  const {
    loanId: transactLoanId,
    loanNumber: transactLoanNumber,
    accountSuffix: transactAccountSuffix,
    email: transactEmail,
  } = transactPayload;
  const effectiveFspId = useMemo(() => {
    const fromRegister = registerData?.payer?.fsp?.id;
    if (fromRegister != null && fromRegister !== '') {
      return fromRegister;
    }
    return fspId;
  }, [registerData, fspId]);

  const loanContext = useMemo(
    () => ({
      loanId: transactLoanId,
      loanNumber: transactLoanNumber,
      accountSuffix: transactAccountSuffix,
      email: transactEmail,
    }),
    [transactAccountSuffix, transactEmail, transactLoanId, transactLoanNumber]
  );

  const [popup, setPopup] = useState(null);
  const [popupError, setPopupError] = useState(null);
  const popupRequestedRef = useRef(false);
  const paymentPopupWindowRef = useRef(null);

  const {
    status,
    error: launchError,
    jwt,
    assertionBaseUrl,
  } = useEnrolledPalLaunch({
    fspId: effectiveFspId,
    payerId: registerData?.payer?.id,
    userId: registerData?.user?.id,
    loanContext,
    preOpenedWindow: popup,
  });
  const error = popupError || launchError;

  useEffect(() => {
    if (status !== 'idle' || popupRequestedRef.current) {
      return;
    }
    popupRequestedRef.current = true;

    const opened = window.open(
      getPopupLoadingUrl(),
      IFP_PAL_PAYMENT_POPUP_WINDOW_NAME,
      buildIfpPalPaymentPopupFeatures()
    );

    if (!opened || opened.closed || opened.closed === undefined) {
      setPopupError(
        'Popup window was blocked. Please allow popups for this site and try again.'
      );
      return;
    }

    try {
      opened.focus();
    } catch (_e) {
      // ignore
    }
    setPopup(opened);
  }, [status]);

  useEffect(() => {
    if (status !== 'error' || !popup) {
      return;
    }
    try {
      popup.close();
    } catch (_e) {
      // ignore
    }
    setPopup(null);
  }, [status, popup]);

  const exitFlow = useCallback(() => {
    if (popup && typeof popup.close === 'function') {
      try {
        popup.close();
      } catch (_e) {
        // ignore
      }
      setPopup(null);
    }
    paymentPopupWindowRef.current = null;
    dispatch(clearTransactAsData());
    onCancel();
  }, [dispatch, onCancel, popup]);

  const handlePopupReady = useCallback((popupWindow) => {
    paymentPopupWindowRef.current = popupWindow;
  }, []);

  const handlePopupClosed = useCallback(() => {
    exitFlow();
  }, [exitFlow]);

  return (
    <>
      <DialogTitle>
        Transact As {payerName} — PAL payment
        <IconButton
          aria-label="Close Transact As Dialog"
          className={classes.closeButton}
          color="inherit"
          onClick={exitFlow}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Box className={classes.body}>
        {(status === 'idle' || status === 'minting') && !error && (
          <Box textAlign="center" py={3}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Preparing secure payment session…
            </Typography>
          </Box>
        )}
        {error && (
          <Box py={2}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
            <Box mt={2}>
              <Button color="primary" onClick={exitFlow} variant="contained">
                Close
              </Button>
            </Box>
          </Box>
        )}
        {status === 'success' && jwt && assertionBaseUrl && (
          <Box py={1}>
            <Typography variant="body2" color="textSecondary" paragraph>
              Complete your payment in the popup window. When you are done, you
              may close the popup or use Close here to return to payer detail.
            </Typography>
            <PaymentIframe
              assertionBaseUrl={assertionBaseUrl}
              fspId={effectiveFspId}
              ifpAssertionVariant="enrolled"
              jwt={jwt}
              onPopupClosed={handlePopupClosed}
              onPopupReady={handlePopupReady}
              preOpenedPopup={popup}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

EnrolledPalAfterRegister.propTypes = {
  payerName: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  fspId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default EnrolledPalAfterRegister;
